import { arushaClient, getIdentityAuth } from '@/client';
import {
  ClientLoaderFunctionArgs,
  data,
  MetaFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from 'react-router';

import { PayleoLogo } from '@/components/logo/payleo';
import { RefreshTokenError, RefreshTokenExpiredError } from '@payleo/auth/errors';
import { WebAuth } from '@payleo/auth/web';
import Arusha from '@payleo/platform/arusha';
import { ArrowRightIcon, UsersIcon } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

export const meta: MetaFunction = () => {
  const baseurl = 'https://manage.payleo.tz';
  const title = 'Manage Payleo';
  const description = 'Access and easily manage your companies finances with Payleo';
  const ogimage = '/assets/images/main-og.jpg';

  return [
    { title },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:image',
      content: ogimage,
    },
    {
      property: 'og:url',
      content: baseurl,
    },
    {
      property: 'og:description',
      content: description,
    },
    {
      name: 'description',
      content: description,
    },
  ];
};

// loads the values and attempts to redirect to available page
const main = arushaClient();

export async function clientLoader({ request }: ClientLoaderFunctionArgs) {
  const auth = getIdentityAuth();
  try {
    if (!auth.currentUser?.session) {
      await auth.refreshSession();
    }
  } catch (err) {
    if (err instanceof RefreshTokenExpiredError || err instanceof RefreshTokenError) {
      await auth.removeAuthState();
      throw redirect('/logout');
    }

    throw err;
  }

  const session = auth.currentUser?.session;
  if (!session) {
    throw redirect('/login');
  }

  if (session.type === 'admin') {
    return redirect('/console/organizations');
  }

  try {
    // TODO: accomodate for pagination when filtering the organizations
    const a = await Arusha.getCompanies(main, { limit: 20 });
    const orgs = a?.data;
    if (orgs.length === 0) {
      return redirect('/onboarding');
    }

    const sp = new URL(request.url);
    if (orgs.length === 1 && !sp.searchParams.has('select')) {
      // NOTE: auto select the first organization
      //  as active organization for the event that the user
      // is only a part of a single organization
      const v = orgs[0];
      return redirect(`/app/${v.domain}`, { status: 301 });
    }

    // const v = orgs.data[0];
    // return redirect(`/${v.url_hash}`);
    return data(orgs);
  } catch (err) {
    if (err instanceof RefreshTokenExpiredError) {
      auth.removeAuthState();
      return redirect(`/logout`, {
        headers: { 'x-app-err-message': 'refresh token expired error' },
      });
    } else if (err instanceof RefreshTokenError) {
      return redirect(`/logout`, { headers: { 'x-app-err-message': 'refresh token invalidated' } });
    }

    throw err;
  }
}

// export function HydrateFallback() {
//   return (
//     <div className="bg-payleo-gray-50/5 flex h-dvh w-full flex-col items-center justify-center">
//       <div className="flex h-[200px] w-full max-w-md flex-row items-center justify-center rounded-xl bg-white drop-shadow-md">
//         <Loader.Indicator />
//         <p className="font-display ml-2 text-lg">Pulling details...</p>
//       </div>
//     </div>
//   );
// }

export default function MainPage() {
  const navigate = useNavigate();
  const auth = useRef<WebAuth>(undefined);
  useEffect(() => {
    if (!auth.current) {
      auth.current = getIdentityAuth();
    }

    if (!auth.current.currentUser) {
      navigate('/login');
    }
  });

  return (
    <div className="relative min-h-dvh bg-gray-50/50">
      <header className="border-payleo-white sticky top-0 z-20 border-b bg-white/60 backdrop-blur-md">
        <div className="container mx-auto max-w-6xl px-4 py-3 xl:px-16">
          <PayleoLogo />
          <div>
            {/* TODO: include the user picker to include logout button + user associated options */}
          </div>
        </div>
      </header>
      <main className="py-4">
        <div className="container mx-auto max-w-6xl px-4 xl:px-16">
          <div className="bg-payleo-white w-full rounded-xl px-8 py-6 [&>p]:mt-3">
            <h2 className="font-display text-2xl font-medium">Among the first. </h2>
            <p className="font-display">
              You are among the who are given have granted access to Payleo's Private Beta program.
              This program is a closed testing program setup to provide first hand exprience with
              the Payleo platform. To read more about this, see{' '}
              <a className="underline" href="#">
                About the Private Beta
              </a>{' '}
              here
            </p>
            <div className="h-36" />
          </div>
        </div>
        <div className="container mx-auto -mt-28 max-w-4xl pb-8">
          <div className="space-y-1 py-4">
            <h4 className="font-display text-lg font-semibold">Select to continue</h4>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <OrganizationsList>
              {(data) => (
                <a
                  href={`/app/${data.domain}`}
                  className="group border-payleo-white/50 hover:border-primary flex h-32 cursor-pointer flex-col rounded-xl border bg-white px-4 py-3.5 drop-shadow-xs transition hover:bg-gray-50"
                >
                  <div>
                    <span className="font-display">{data.name}</span>
                  </div>
                  <div className="flex-1" />
                  <div className="flex flex-row items-baseline justify-between">
                    <span className="text-payleo-gray-500 inline-flex flex-row items-center gap-1">
                      <UsersIcon className="size-3" />
                      <label className="text-xs">{data.member_count}</label>
                    </span>
                    <button className="text-payleo-gray-50 group-hover:border-primary inline-flex size-6 items-center justify-center rounded-full border border-transparent transition">
                      <ArrowRightIcon className="group-hover:text-primary-foreground h-auto w-4" />
                    </button>
                  </div>
                </a>
              )}
            </OrganizationsList>
          </div>
        </div>
      </main>
    </div>
  );
}

function OrganizationsList({ children: Children }: { children: React.FC }) {
  const orgs = useLoaderData<typeof clientLoader>();

  return (
    <>
      {orgs.map((o) => (
        <Children key={o.id} {...o} />
      ))}
    </>
  );
}
